import { Fragment, RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { formatCurrencyCompact, getCurrency } from '../../../../helpers/currency-helpers';
import { truncateLabel } from '../../../../helpers/string-helpers/string-helpers';
import { useFinancialsStore } from '../../../../store/financials-store/financials-store';
import type { CostVersusOutputMeasures } from '../../../../store/financials-store/financials-store.type';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { COST_VERSUS_OUTPUT_MEASURES, DEFAULT_CHART_COLORS } from '../../constants';
import type { DataPoint } from '../overview.type';
import { CustomTooltip } from '../shared/custom-tooltip';
import { Legend as CostVersusOutputLegend } from '../shared/Legend';
import { assignColorsToItems, formatXAxisLabel, getItemColor } from '../shared/shared.helpers';
import { formatChartTooltipValue, getMeasureName } from './cost-versus-output.helpers';

function Chart({ chartData, chartRef }: { chartData: DataPoint[]; chartRef: RefObject<HTMLDivElement> }) {
  const { t } = useTranslation();
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const organization = useGlobalStore((state) => state.organization);
  const { code: currencyCode } = getCurrency(organization);
  const selectedMeasures = useFinancialsStore((state) => state.filters.costVersusOutput.selectedMeasures);
  const selectedTrendLines = useFinancialsStore((state) => state.filters.costVersusOutput.selectedTrendLines);
  const lineConfigs = [
    { dataKey: 'cost', yAxisId: 'cost' },
    { dataKey: 'daily_burn', yAxisId: 'cost' },
    { dataKey: 'cost_per_point', yAxisId: 'cost' },
    { dataKey: 'cost_per_task', yAxisId: 'cost' },
    { dataKey: 'committed_points', yAxisId: 'discrete' },
    { dataKey: 'completed_points', yAxisId: 'discrete' },
    { dataKey: 'committed_tasks', yAxisId: 'discrete' },
    { dataKey: 'completed_tasks', yAxisId: 'discrete' },
  ] as const;

  const colorAssignments = assignColorsToItems(selectedMeasures, DEFAULT_CHART_COLORS);

  return (
    <div ref={chartRef} style={{ width: '100%', height: '100%', minHeight: 160 }}>
      <ResponsiveContainer>
        <ComposedChart data={chartData}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="date" tickFormatter={formatXAxisLabel} tick={{ fontSize: 12 }} />
          <YAxis
            yAxisId="cost"
            orientation="left"
            tick={{ fontSize: 12 }}
            tickFormatter={(value) => formatCurrencyCompact(value, { currency: currencyCode })}
          />
          <YAxis yAxisId="discrete" orientation="right" tick={{ fontSize: 12 }} />
          <Tooltip
            content={
              <CustomTooltip
                filterFn={(item) => COST_VERSUS_OUTPUT_MEASURES.includes(item.dataKey as CostVersusOutputMeasures)}
                nameFormatter={(item) => truncateLabel(getMeasureName(item.dataKey as CostVersusOutputMeasures, t))}
                valueFormatter={(item) =>
                  formatChartTooltipValue(Number(item.value), String(item.dataKey), currencyCode, t)
                }
              />
            }
          />
          <Legend content={<CostVersusOutputLegend hoveredItem={hoveredItem} setHoveredItem={setHoveredItem} />} />
          {lineConfigs.map(({ dataKey, yAxisId }) => {
            const color = getItemColor(dataKey, colorAssignments);
            return (
              <Fragment key={dataKey}>
                <Bar
                  yAxisId={yAxisId}
                  type="monotone"
                  dataKey={dataKey}
                  stroke={color}
                  fill={color}
                  opacity={hoveredItem === null || hoveredItem === dataKey ? 1 : 0.3}
                  hide={!selectedMeasures.includes(dataKey)}
                />
                <Line
                  yAxisId={yAxisId}
                  type="monotone"
                  dataKey={`${dataKey}_trend`}
                  stroke={color}
                  strokeWidth={hoveredItem === null || hoveredItem === dataKey ? 2 : 1}
                  opacity={hoveredItem === null || hoveredItem === dataKey ? 1 : 0.3}
                  strokeDasharray="5 5"
                  hide={!selectedMeasures.includes(dataKey) || !selectedTrendLines.includes(dataKey)}
                />
              </Fragment>
            );
          })}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export { Chart };
